import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Stack, Collapse, ListItem,
  ListItemIcon, Checkbox, FormControlLabel, Paper, Divider, IconButton, Chip
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowUp,
  RadioButtonChecked, RadioButtonUnchecked,
  AutoAwesome, HistoryEdu, ListAlt
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { EXAMS } from '../config/categories';

const MotionBox = motion(Box);

export default function ConfirmStudyPlan({
  open, onClose, subjects, selectedContent, expandedSubjects,
  toggleExpand, toggleChapter, toggleTopic, confirmAndSave,
  saving, selectedExam
}) {
  const totalHours = subjects.reduce((a, b) => a + b.estimatedHours, 0);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '16px', p: 1 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ListAlt color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={900}>Confirm Your Plan</Typography>
            <Typography variant="caption" color="text.secondary">
              Review and customize your {EXAMS[selectedExam]?.label} roadmap
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>Prep Structure</Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {subjects.map((s, i) => {
                const subjDef = (EXAMS[selectedExam] ? EXAMS[selectedExam].subjects : [])?.find(x => ((typeof x === 'string') ? x : x.title) === s.title);
                const sel = selectedContent[s.title] || { chapters: null, selected: true };
                const isExpanded = Boolean(expandedSubjects[s.title]);

                return (
                  <Paper
                    key={s.title}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '16px',
                      overflow: 'hidden'
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'rgba(45, 91, 255, 0.02)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(45, 91, 255, 0.04)' }
                      }}
                      onClick={() => toggleExpand(s.title)}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography fontWeight={800} color="primary" sx={{ opacity: 0.6 }}>{String(i + 1).padStart(2, '0')}</Typography>
                        <Typography fontWeight={700}>{s.title}</Typography>
                      </Stack>
                      <IconButton size="small">
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </Stack>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                        {subjDef && typeof subjDef !== 'string' ? (
                          (subjDef.chapters || []).map((ch) => (
                            <Box key={ch.id} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={Boolean(sel.chapters?.[ch.id]?.selected)}
                                    onChange={(e) => { e.stopPropagation(); toggleChapter(s.title, ch.id); }}
                                    icon={<RadioButtonUnchecked fontSize="small" />}
                                    checkedIcon={<RadioButtonChecked fontSize="small" />}
                                  />
                                }
                                label={<Typography variant="body2" fontWeight={700}>{ch.title}</Typography>}
                              />
                              <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                {(ch.topics || []).map((t) => (
                                  <Chip
                                    key={t}
                                    label={t}
                                    size="small"
                                    onClick={() => toggleTopic(s.title, ch.id, t)}
                                    color={sel.chapters?.[ch.id]?.topics?.[t] ? "primary" : "default"}
                                    variant={sel.chapters?.[ch.id]?.topics?.[t] ? "filled" : "outlined"}
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: 24,
                                      borderRadius: '16px',
                                      fontWeight: 600
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Full subject content selected.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>Included Features</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 2, borderStyle: 'dashed' }}>
                <AutoAwesome color="primary" />
                <Typography variant="body2">Adaptive Assessment Powered by <b>Questguru GenAI</b></Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 2, borderStyle: 'dashed' }}>
                <HistoryEdu color="primary" />
                <Typography variant="body2">Comprehensive <b>Previous Year papers</b> analysis</Typography>
              </Paper>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} sx={{ fontWeight: 700 }}>Back</Button>
        <Button
          variant="contained"
          onClick={confirmAndSave}
          disabled={saving}
          fullWidth
          size="large"
          sx={{
            borderRadius: '16px',
            fontWeight: 800,
            py: 1.5,
            boxShadow: '0 10px 20px rgba(45, 91, 255, 0.2)'
          }}
        >
          {saving ? 'Creating Path...' : 'Confirm & Start Journey'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
